import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { BookOpen, ChevronDown, ChevronUp, Search, X, Lock } from "lucide-react";

function useDebounced(value, delay = 250) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

function formatDate(iso) {
  // iso = YYYY-MM-DD ; render as "Friday, June 26"
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

function formatYear(iso) {
  if (!iso) return "";
  return iso.slice(0, 4);
}

function ReadingCard({ entry, expanded, onToggle, isToday }) {
  const hasNotes = (entry.verse_notes && entry.verse_notes.length > 0) || (entry.reflection && entry.reflection.trim());

  return (
    <article
      id={`day-${entry.day_number}`}
      data-testid={`reading-card-${entry.day_number}`}
      className="card-surface p-7 lg:p-9 transition-colors scroll-mt-32"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        {/* Day + Date column */}
        <div className="shrink-0">
          <div className="overline mb-2" style={{ fontSize: "0.65rem", color: "var(--gold)" }}>
            Day {entry.day_number}
          </div>
          <div className="font-serif-display text-2xl lg:text-3xl tracking-[-0.02em] leading-tight" style={{ color: "var(--ink)" }}>
            {formatDate(entry.date)}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{formatYear(entry.date)}</div>
        </div>

        {/* Passage column */}
        <div className="flex-1 min-w-[16rem]">
          {entry.title && (
            <h2 className="font-serif-display text-2xl lg:text-3xl tracking-[-0.02em] leading-tight mb-3" style={{ color: "var(--ink)" }}>
              {entry.title}
            </h2>
          )}
          <div className="overline mb-2" style={{ fontSize: "0.6rem" }}>Today's reading</div>
          <h3 className="font-serif-display-italic text-2xl lg:text-3xl leading-tight" style={{ color: "var(--ember)" }}>
            {entry.passage}
          </h3>
          {entry.key_scripture && (
            <p className="mt-2 font-serif-display-italic text-base lg:text-lg" style={{ color: "var(--terracotta)" }}>
              {entry.key_scripture}
            </p>
          )}

          {isToday && (
            <span className="inline-block mt-3 px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.18em]" style={{ background: "var(--ember)", color: "var(--bg-base)" }}>
              Today
            </span>
          )}
        </div>
      </div>

      {hasNotes ? (
        <>
          <button
            data-testid={`reading-toggle-${entry.day_number}`}
            onClick={onToggle}
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
            style={{ color: "var(--ember)" }}
          >
            {expanded ? <>Hide notes <ChevronUp size={12} /></> : <>Read Cody's notes <ChevronDown size={12} /></>}
          </button>

          {expanded && (
            <div className="mt-6 pt-6 border-t space-y-6" style={{ borderColor: "var(--border-subtle)" }}>
              {entry.verse_notes && entry.verse_notes.length > 0 && (
                <div className="space-y-5">
                  <div className="overline" style={{ fontSize: "0.65rem" }}>Verse notes</div>
                  {entry.verse_notes.map((vn, i) => (
                    <div key={i} data-testid={`reading-${entry.day_number}-verse-${i}`} className="border-l-2 pl-4" style={{ borderColor: "var(--terracotta)" }}>
                      <div className="font-serif-display-italic text-base" style={{ color: "var(--terracotta)" }}>{vn.ref}</div>
                      <p className="mt-2 text-sm lg:text-base leading-[1.75] whitespace-pre-line" style={{ color: "var(--ink-2)" }}>
                        {vn.note}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {entry.reflection && entry.reflection.trim() && (
                <div data-testid={`reading-${entry.day_number}-reflection`}>
                  <div className="overline mb-3" style={{ fontSize: "0.65rem" }}>Reflection</div>
                  <p className="text-sm lg:text-base leading-[1.85] whitespace-pre-line" style={{ color: "var(--ink-2)" }}>
                    {entry.reflection}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="mt-6 text-xs italic" style={{ color: "var(--muted)" }}>
          Notes coming after Cody completes the reading.
        </p>
      )}
    </article>
  );
}

export default function ReadWithMe() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounced(searchInput, 200);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .get("/members/reading-plan")
      .then((res) => setEntries(res.data || []))
      .catch((e) => setError(e?.response?.status === 403 ? "membership" : "unknown"))
      .finally(() => setLoading(false));
  }, [user]);

  // Auto-scroll to ?#day-N hash once entries render
  useEffect(() => {
    if (loading || entries.length === 0) return;
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      // Auto-expand it
      const dayMatch = hash.match(/day-(\d+)/);
      if (dayMatch) {
        const day = parseInt(dayMatch[1]);
        const entry = entries.find((e) => e.day_number === day);
        if (entry) setExpandedId(entry.id);
      }
    }
  }, [loading, entries]);

  const today = new Date().toISOString().slice(0, 10);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <h1 className="font-serif-display text-3xl mt-6" style={{ color: "var(--ink)" }}>Sign in to continue</h1>
        <p className="mt-4" style={{ color: "var(--ink-2)" }}>Read With Me is a members-only journey through Scripture.</p>
        <Link to="/login?next=/read-with-me" data-testid="rwm-login-redirect" className="btn-primary mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  if (error === "membership" || (!loading && !user.membership_active && !user.is_admin)) {
    return (
      <div data-testid="rwm-paywall" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <div className="overline mt-6">Members only</div>
        <h1 className="font-serif-display text-4xl mt-3" style={{ color: "var(--ink)" }}>Read With Me is for members.</h1>
        <p className="mt-5 leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Walk through the Bible with Cody — every passage, every verse note, every reflection, day by day.
          Join The Disciple Life for $4.99/month.
        </p>
        <Link to="/membership" data-testid="rwm-subscribe-link" className="btn-primary mt-8 inline-flex">Become a member</Link>
      </div>
    );
  }

  const filtered = debouncedSearch
    ? entries.filter((e) => {
        const q = debouncedSearch.toLowerCase();
        return (
          (e.passage || "").toLowerCase().includes(q) ||
          String(e.day_number).includes(q) ||
          (e.title || "").toLowerCase().includes(q) ||
          (e.key_scripture || "").toLowerCase().includes(q) ||
          (e.reflection || "").toLowerCase().includes(q) ||
          (e.verse_notes || []).some((vn) =>
            (vn.ref || "").toLowerCase().includes(q) || (vn.note || "").toLowerCase().includes(q)
          )
        );
      })
    : entries;

  const todayEntry = entries.find((e) => e.date === today);
  const archive = filtered.filter((e) => e.id !== todayEntry?.id);

  return (
    <div data-testid="read-with-me-page" className="max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
      {/* Header */}
      <header className="mb-14 lg:mb-20">
        <div className="overline mb-3">A daily walk through Scripture</div>
        <h1 className="font-serif-display text-5xl lg:text-7xl tracking-[-0.03em] leading-[0.95]" style={{ color: "var(--ink)" }}>
          Read with <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>me</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base lg:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Walking through the Bible one day at a time. Each entry shows the day's reading, the verses that struck me, and the reflection I sat with afterward — kept here just for members.
        </p>
        <div className="mt-8 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gold)" }}>
          <BookOpen size={14} strokeWidth={2} />
          <span>{entries.length} day{entries.length === 1 ? "" : "s"} published so far</span>
        </div>
      </header>

      {/* Today */}
      {todayEntry && !debouncedSearch && (
        <section className="mb-16">
          <div className="overline mb-5" style={{ color: "var(--ember)" }}>Today</div>
          <ReadingCard
            entry={todayEntry}
            expanded={expandedId === todayEntry.id}
            onToggle={() => setExpandedId(expandedId === todayEntry.id ? null : todayEntry.id)}
            isToday
          />
        </section>
      )}

      {/* Search */}
      {entries.length > 0 && (
        <div className="mb-10">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
            <input
              data-testid="read-with-me-search"
              type="text"
              placeholder="Search by passage, verse, day number, or keyword…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-11 pr-10 py-3 text-sm bg-transparent border outline-none focus:border-[var(--ember)] transition-colors"
              style={{ borderColor: "var(--border-subtle)", color: "var(--ink)" }}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                data-testid="read-with-me-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                style={{ color: "var(--muted)" }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Archive */}
      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : entries.length === 0 ? (
        <div className="card-surface p-16 text-center" data-testid="read-with-me-empty">
          <BookOpen size={32} strokeWidth={1.5} style={{ color: "var(--muted)", margin: "0 auto" }} />
          <h3 className="font-serif-display text-2xl mt-5" style={{ color: "var(--ink)" }}>
            The walk hasn't begun yet.
          </h3>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
            Check back soon — the first day publishes June 26, 2026.
          </p>
        </div>
      ) : (
        <>
          {!debouncedSearch && archive.length > 0 && (
            <div className="overline mb-6">The archive</div>
          )}
          {debouncedSearch && (
            <div className="overline mb-6">
              {filtered.length} result{filtered.length === 1 ? "" : "s"}
              {filtered.length === 0 && " — try a different keyword"}
            </div>
          )}

          {(debouncedSearch ? filtered : archive).length === 0 && debouncedSearch ? (
            <div className="card-surface p-12 text-center">
              <p style={{ color: "var(--muted)" }}>No readings match "{debouncedSearch}" yet.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(debouncedSearch ? filtered : archive).map((e) => (
                <ReadingCard
                  key={e.id}
                  entry={e}
                  expanded={expandedId === e.id}
                  onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
                  isToday={e.date === today}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
